import { Injectable, inject } from '@angular/core';
import { Observable, map, tap } from 'rxjs';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FirestoreProjectService } from './firestore-project.service';
import { FirestoreFieldExecutionProjectService } from './firestore-field-execution-project.service';
import { Project } from '../models/project.model';
import { BLOCK_GP_MASTER, GP_NAME_ALIASES } from '../constants/field-execution-master-data';
import { aggregateFeStats } from '../utils/field-execution-stats.utils';
import { splitExecutantNames } from '../utils/executant-name.utils';

/** Fixed block list for Field Execution */
export const FIELD_EXECUTION_BLOCKS = [
    'Dhamanagar',
    'Tihidi',
    'Dhamanagar NAC',
    'Tihidi NAC',
    'Dhusuri NAC',
] as const;



export interface VillageGroup {
    village: string;
    projects: Project[];
    totalProjects: number;
    sanctionedAmount: number;
    onTrackCount: number;
    onTrackPercent: number;
    inProgressCount: number;
    inProgressPercent: number;
    delayedCount: number;
    delayedPercent: number;
    avgProgress: number;
    paiScore: number;
    paiCategory: 'Good' | 'Moderate' | 'Poor';
    paiTrend: 'up' | 'down';
    lastUpdated?: string;
    statusBadge?: 'High Delay' | 'Good' | 'Poor' | 'Moderate';
}

export interface BlockGroup {
    block: string;
    villages: VillageGroup[];
    totalProjects: number;
    sanctionedAmount: number;
    onTrackCount: number;
    onTrackPercent: number;
    inProgressCount: number;
    inProgressPercent: number;
    delayedCount: number;
    delayedPercent: number;
    avgProgress: number;
    avgPaiScore: number;
    lastUpdated?: string;
    statusBadge?: 'High Delay' | 'Good Progress' | 'Low Performance';
}

@Injectable({ providedIn: 'root' })
export class FieldExecutionService {
    private readonly hmProjectService = inject(FirestoreProjectService);
    private readonly feProjectService = inject(FirestoreFieldExecutionProjectService);
    private nacMigrationDone = false;
    private hmMigrationDone = false;
    private summaryCleanupDone = false;

    /** Deduplicated FE project stream — same rows used for block/GP grouping. */
    getProjects$(): Observable<Project[]> {
        return this.fieldExecutionProjects$();
    }

    /** Field Execution projects from the dedicated `field_execution_projects` collection. */
    private fieldExecutionProjects$(): Observable<Project[]> {
        const norm = (s: string) => (s || '').toLowerCase().replace(/\s+/g, ' ').trim();

        return this.feProjectService.getProjects$().pipe(
            tap(projects => {
                if (!this.hmMigrationDone) {
                    this.hmMigrationDone = true;
                    this.hmProjectService.getProjects$().subscribe({
                        next: hm => {
                            this.feProjectService.migrateFromHmCollection(hm).catch(() => { /* non-fatal */ });
                        },
                    });
                }
                if (!this.nacMigrationDone && projects.length > 0) {
                    this.nacMigrationDone = true;
                    this.feProjectService.migrateNacProjects(projects).catch(() => { /* non-fatal */ });
                }
                if (!this.summaryCleanupDone && projects.length > 0) {
                    this.summaryCleanupDone = true;
                    this.feProjectService.cleanupSummaryImportRows(projects).catch(() => { /* non-fatal */ });
                }
            }),
            map(projects => {
                const isNacGpUnderDhamanagar = (p: Project) => {
                    const b = (p.block ?? '').toLowerCase();
                    if (b !== 'dhamanagar' && b !== 'dhamnagar') return false;
                    const v = (p.village || p.loc || '').toLowerCase().trim();
                    return v === 'nac' || v.includes('dhamnagar nac') || v.includes('dhamanagar nac');
                };

                const normalized = projects
                    .map(p => ({ ...p, block: this.normalizeBlockName(p) }))
                    .filter(p => !isNacGpUnderDhamanagar(p));

                // Pass 1: dedup by Firestore doc ID
                const idSeen = new Set<string>();
                const idDeduped = normalized.filter(p => {
                    if (!p.id || idSeen.has(p.id)) return false;
                    idSeen.add(p.id);
                    return true;
                });

                // Pass 2: content dedup — same project imported multiple times
                // with different doc IDs (name + scheme + village + block as key).
                const contentSeen = new Set<string>();
                return idDeduped.filter(p => {
                    const ck = p.projectId
                        ? norm(p.projectId)
                        : [
                            norm(p.name),
                            norm(p.scheme ?? ''),
                            norm(p.village || p.loc || ''),
                            norm(p.block ?? ''),
                          ].join('||');
                    if (contentSeen.has(ck)) return false;
                    contentSeen.add(ck);
                    return true;
                });
            })
        );
    }

    private normalizeBlockName(p: Project): string {
        const b = (p.block || '').trim();
        const v = (p.village || p.loc || '').toLowerCase();
        
        // Consolidate all variations of "Nac" into "Dhamanagar NAC"
        if (b.toLowerCase() === 'nac' || v === 'nac' || v.includes('dnr, nac') || v.includes('dhamnagar nac')) {
            return 'Dhamanagar NAC';
        }
        
        // Fix typos
        if (b === 'Dhamnagar') return 'Dhamanagar';
        
        return b;
    }




    /** Real-time stream of all projects grouped Block → Village.
     *  All departments are always present, even with zero projects. */
    getBlockGroups$(): Observable<BlockGroup[]> {
        return this.fieldExecutionProjects$().pipe(
            map(projects => this.groupByBlockVillage(projects))
        );
    }

    /** All distinct block names from imported data */
    getBlocks$(): Observable<string[]> {
        return this.fieldExecutionProjects$().pipe(
            map(projects => {
                const blocks = new Set(projects.map(p => p.block ?? '').filter(Boolean));
                return Array.from(blocks).sort();
            })
        );
    }

    /** All projects (raw) for a given block */
    getProjectsByBlock$(block: string): Observable<Project[]> {
        return this.fieldExecutionProjects$().pipe(
            map(projects => projects.filter(p => p.block === block))
        );
    }

    /**
     * Returns all field-execution projects for a given executant key.
     * Uses the same block-filtered dataset as getBlockGroups$ so the
     * detail page always matches what the list page shows.
     *
     * Key format:
     *   "number:<executorNumber>"  — primary lookup by mobile/contact number
     *   "name:<encodedName>"       — fallback lookup by name (no phone registered)
     *   "person:<encodedName>"     — individual member of a (possibly joint)
     *                                executant entry; matches every project whose
     *                                executant list contains this name, whether
     *                                solo or in a collaboration ("A & B", "A , B")
     */
    getProjectsByExecutantKey$(key: string): Observable<Project[]> {
        return this.fieldExecutionProjects$().pipe(
            map(allProjects => {
                const projects = allProjects
                    .map(p => ({ ...p, block: this.normalizeBlockName(p) }));

                let matched: Project[];

                // Canonical name extractor — checks all three name fields
                const execName = (p: Project) =>
                    (p.executorName ?? p.executingAgency ?? p.executant?.name ?? '').trim().toLowerCase();

                // Raw (uncased) name extractor for person-level splitting
                const execNameRaw = (p: Project) =>
                    p.executorName ?? p.executingAgency ?? p.executant?.name ?? '';

                if (key.startsWith('person:')) {
                    const person = decodeURIComponent(key.slice(7)).trim().toLowerCase();
                    matched = person
                        ? projects.filter(p =>
                            splitExecutantNames(execNameRaw(p))
                                .some(n => n.toLowerCase() === person))
                        : [];
                } else if (key.startsWith('name:')) {
                    const name = decodeURIComponent(key.slice(5)).trim().toLowerCase();
                    matched = projects.filter(p => execName(p) === name);
                } else if (key.startsWith('number:')) {
                    // Legacy: resolve number → name, then do name-based lookup
                    const num = key.slice(7).trim();
                    const seed = projects.find(p =>
                        (p.executorNumber ?? '').trim() === num ||
                        (p.executant?.contact ?? '').trim() === num
                    );
                    if (seed) {
                        const name = execName(seed);
                        matched = name ? projects.filter(p => execName(p) === name) : [];
                    } else {
                        matched = [];
                    }
                } else {
                    return [];
                }

                // Pass 1: dedup by Firestore doc ID
                const idSeen = new Set<string>();
                const idDeduped = matched.filter(p => {
                    if (!p.id || idSeen.has(p.id)) return false;
                    idSeen.add(p.id);
                    return true;
                });

                // Pass 2: content dedup — handles the case where the same project
                // was imported multiple times (different IDs, identical data).
                // Uses projectId when available; otherwise name+scheme+cost
                // (location fields deliberately excluded — they can vary across
                // duplicate imports and would defeat the dedup).
                const norm = (s: string) =>
                    (s || '').toLowerCase().replace(/\s+/g, ' ').trim();
                const contentSeen = new Set<string>();
                return idDeduped.filter(p => {
                    const ck = p.projectId
                        ? norm(p.projectId)
                        : [norm(p.name), norm(p.scheme ?? ''), String(p.cost ?? 0)].join('||');
                    if (contentSeen.has(ck)) return false;
                    contentSeen.add(ck);
                    return true;
                });
            })
        );
    }

    /** Update editable fields on a field-execution project row. */
    async updateProject(id: string, updates: Partial<Pick<Project, 'status' | 'remarks' | 'executorName' | 'executorNumber' | 'physical' | 'progress' | 'cost' | 'spent'>>): Promise<void> {
        await this.feProjectService.updateProject(id, updates);
    }

    /** Export executant report to XLSX */
    exportExecutantToExcel(
        projects: Project[],
        executantName: string,
        executantNumber: string
    ): void {
        const rows = projects.map((p, i) => ({
            'Sl.':              i + 1,
            'Project Name':     p.name,
            'Scheme':           p.scheme || '',
            'Sanctioned Year':  p.sanctionedYear || '',
            'Village':          p.village || p.loc || '',
            'Block':            p.block || '',
            'Department':       p.dept || '',
            'Est. Cost (₹L)':  p.cost ?? 0,
            'Status':           p.status,
            'Start Date':       p.start || '',
            'Target Date':      p.targetDate || p.end || '',
            'Remarks':          p.remarks || '',
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        ws['!cols'] = [
            { wch: 4 }, { wch: 45 }, { wch: 18 }, { wch: 16 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 12 }, { wch: 28 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Executant Report');
        const safe = (executantNumber || executantName || 'executant').replace(/[^a-zA-Z0-9_-]/g, '_');
        XLSX.writeFile(wb, `executant-report-${safe}.xlsx`);
    }

    /** Export executant detail as a professional PDF report (A4 portrait, auto multi-page). */
    exportExecutantToPdf(params: {
        name: string; number: string; block: string; gp: string; type: string;
        totalProjects: number; totalCost: number; riskLevel: string; riskScore: number;
        completedCount: number; inProgressCount: number; stuckCount: number; plannedCount: number;
        lastUpdatedDays: number | null; projects: Project[];
    }): void {
        const { name, number, block, gp, type, totalProjects, totalCost,
                riskLevel, riskScore, completedCount, inProgressCount,
                stuckCount, plannedCount, lastUpdatedDays, projects } = params;

        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pw = doc.internal.pageSize.getWidth();
        const ph = doc.internal.pageSize.getHeight();

        // Palette (matches dashboard theme)
        const NAV:   [number,number,number] = [30,  41,  59 ];
        const ACCENT:[number,number,number] = [59,  130, 246];
        const GREEN: [number,number,number] = [22,  163, 74 ];
        const AMBER: [number,number,number] = [217, 119, 6  ];
        const RED:   [number,number,number] = [220, 38,  38 ];
        const SLATE: [number,number,number] = [100, 116, 139];
        const BGROW: [number,number,number] = [248, 250, 252];
        const BORD:  [number,number,number] = [203, 213, 225];

        // ── 1. HEADER ─────────────────────────────────────────────────────────
        doc.setFillColor(...NAV);
        doc.rect(0, 0, pw, 22, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(13);
        doc.text('D3 DHAMNAGAR DEVELOPMENT DASHBOARD', pw / 2, 9, { align: 'center' });
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text('Executant Detailed Report  |  Government of Odisha', pw / 2, 15.5, { align: 'center' });
        doc.setFillColor(...ACCENT);
        doc.rect(0, 22, pw, 1, 'F');
        doc.setTextColor(...SLATE);
        doc.setFontSize(7.5);
        doc.text(
            `Generated: ${new Date().toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}`,
            pw - 10, 27, { align: 'right' }
        );

        let y = 30;

        // ── 2. PROFILE CARD ───────────────────────────────────────────────────
        doc.setFillColor(...NAV);
        doc.circle(22, y + 9, 9, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        const initials = (name || 'EX').split(/\s+/).map((w: string) => w[0] || '').join('').slice(0, 2).toUpperCase();
        doc.text(initials, 22, y + 12.5, { align: 'center' });

        doc.setTextColor(15, 23, 42);
        doc.setFontSize(13);
        doc.text(name || '—', 36, y + 7);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.setTextColor(...SLATE);
        doc.text(type, 36, y + 13);

        const riskColor: [number,number,number] =
            riskLevel === 'HIGH' ? RED : riskLevel === 'MEDIUM' ? AMBER : GREEN;
        doc.setFillColor(...riskColor);
        doc.roundedRect(pw - 42, y + 1, 32, 10, 2, 2, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.text(`${riskLevel} RISK  (${riskScore}/100)`, pw - 26, y + 7.5, { align: 'center' });
        y += 23;

        // Profile key-value grid (2 rows × 3 cols)
        const lastUpdLabel = lastUpdatedDays === null ? 'N/A'
            : lastUpdatedDays === 0 ? 'Today'
            : `${lastUpdatedDays} day${lastUpdatedDays !== 1 ? 's' : ''} ago`;
        const profileFields: [string, string][] = [
            ['Contact',        number       || '—'],
            ['Block',          block        || '—'],
            ['Primary GP',     gp           || '—'],
            ['Total Projects', String(totalProjects)],
            ['Est. Cost',      `${totalCost.toLocaleString('en-IN')} L`],
            ['Last Updated',   lastUpdLabel],
        ];
        const colsGrid = 3;
        const cellW = (pw - 20) / colsGrid;
        profileFields.forEach(([label, value], idx) => {
            const col = idx % colsGrid;
            const row = Math.floor(idx / colsGrid);
            const cx  = 10 + col * cellW;
            const cy  = y + row * 10;
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7.5);
            doc.setTextColor(...SLATE);
            doc.text(label, cx, cy);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(15, 23, 42);
            doc.text(value, cx, cy + 5);
        });
        y += 24;

        doc.setDrawColor(...BORD);
        doc.setLineWidth(0.3);
        doc.line(10, y, pw - 10, y);
        y += 5;

        // ── 3. STATUS SUMMARY BAND ────────────────────────────────────────────
        const statItems: { label: string; value: number; color: [number,number,number] }[] = [
            { label: 'Completed',   value: completedCount,  color: GREEN },
            { label: 'In Progress', value: inProgressCount, color: AMBER },
            { label: 'Delayed',     value: stuckCount,      color: RED   },
            { label: 'Not Started', value: plannedCount,    color: SLATE },
        ];
        const cardW = (pw - 20 - 9) / 4;
        statItems.forEach(({ label, value, color }, i) => {
            const cx = 10 + i * (cardW + 3);
            const lightBg = color.map(c => Math.min(255, c + 200)) as [number,number,number];
            doc.setFillColor(...lightBg);
            doc.roundedRect(cx, y, cardW, 14, 2, 2, 'F');
            doc.setFillColor(...color);
            doc.rect(cx, y, 2, 14, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(15);
            doc.setTextColor(...color);
            doc.text(String(value), cx + 9, y + 9);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(7);
            doc.setTextColor(...SLATE);
            doc.text(label, cx + 9, y + 13);
        });
        y += 20;

        // Section title
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.setTextColor(15, 23, 42);
        doc.text('PROJECT DETAILS', 10, y);
        doc.setFillColor(...ACCENT);
        doc.rect(10, y + 1.5, 32, 0.8, 'F');
        y += 7;

        // ── 4. PROJECT TABLE (auto multi-page) ────────────────────────────────
        const statusColor = (s: string): [number,number,number] =>
            s === 'Completed'   ? GREEN :
            s === 'In Progress' ? AMBER :
            s === 'Stuck'       ? RED   : SLATE;

        const fmtDate = (d: any): string => {
            if (!d) return '—';
            try {
                const dt = typeof d === 'string' ? new Date(d)
                    : d instanceof Date ? d
                    : typeof d?.toDate === 'function' ? d.toDate() : null;
                if (!dt || isNaN(dt.getTime())) return String(d || '—');
                return dt.toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
            } catch { return '—'; }
        };

        const fmtAge = (ts: any): string => {
            let ms: number | null = null;
            if (!ts) return '—';
            if (typeof ts?.toDate === 'function')    ms = ts.toDate().getTime();
            else if (typeof ts?.seconds === 'number') ms = ts.seconds * 1000;
            else if (ts instanceof Date)              ms = ts.getTime();
            else if (typeof ts === 'number')          ms = ts;
            else if (typeof ts === 'string')          ms = new Date(ts).getTime();
            if (!ms || isNaN(ms)) return '—';
            const d = Math.floor((Date.now() - ms) / 86400000);
            return d === 0 ? 'Today' : `${d}d ago`;
        };

        autoTable(doc, {
            startY: y,
            margin: { left: 10, right: 10 },
            tableWidth: pw - 20,
            head: [['#', 'Project ID', 'Project Name', 'GP / Village',
                     'Scheme', 'Status', 'Cost (₹L)', 'Spent (₹L)',
                     'Start', 'Updated']],
            body: projects.map((p, i) => [
                i + 1,
                p.projectId || '—',
                p.name       || '—',
                p.gp || p.village || p.loc || '—',
                p.scheme     || '—',
                p.status     || '—',
                (p.cost  ?? 0).toFixed(2),
                (p.spent ?? 0).toFixed(2),
                fmtDate(p.start),
                fmtAge((p as any).updatedAt ?? (p as any).photoUploadedAt),
            ]),
            styles: {
                font: 'helvetica',
                fontSize: 7.5,
                cellPadding: { top: 2.5, right: 3, bottom: 2.5, left: 3 },
                lineColor: BORD,
                lineWidth: 0.2,
                textColor: [30, 41, 59],
                overflow: 'linebreak',
            },
            headStyles: {
                fillColor: NAV,
                textColor: [255, 255, 255],
                fontStyle: 'bold',
                fontSize: 7.5,
                halign: 'center',
            },
            alternateRowStyles: { fillColor: BGROW },
            columnStyles: {
                0: { halign: 'center', cellWidth: 7  },
                1: { halign: 'center', cellWidth: 17 },
                2: { cellWidth: 42                   },
                3: { cellWidth: 22                   },
                4: { cellWidth: 20                   },
                5: { halign: 'center', cellWidth: 20 },
                6: { halign: 'right',  cellWidth: 16 },
                7: { halign: 'right',  cellWidth: 14 },
                8: { halign: 'center', cellWidth: 19 },
                9: { halign: 'center', cellWidth: 13 },
            },
            didDrawCell: (data: any) => {
                if (data.section !== 'body' || data.column.index !== 5) return;
                const status = String(data.cell.raw ?? '');
                const [r, g, b] = statusColor(status);
                doc.setFillColor(r, g, b);
                doc.setGState(new (doc as any).GState({ opacity: 0.15 }));
                doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
                doc.setGState(new (doc as any).GState({ opacity: 1   }));
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(7);
                doc.setTextColor(r, g, b);
                doc.text(status,
                    data.cell.x + data.cell.width / 2,
                    data.cell.y + data.cell.height / 2 + 1,
                    { align: 'center' }
                );
            },
            didDrawPage: (data: any) => {
                const totalPages = (doc.internal as any).getNumberOfPages();
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(7);
                doc.setTextColor(...SLATE);
                doc.text(
                    `Executant: ${name}  |  Page ${data.pageNumber} of ${totalPages}  |  Confidential — D3 Dashboard`,
                    pw / 2, ph - 6, { align: 'center' }
                );
                doc.setDrawColor(...BORD);
                doc.setLineWidth(0.2);
                doc.line(10, ph - 9, pw - 10, ph - 9);
            },
        });

        const safe = (number || name || 'executant').replace(/[^a-zA-Z0-9_-]/g, '_');
        doc.save(`executant-report-${safe}.pdf`);
    }

    /** Export a list of projects to XLSX */
    exportToExcel(projects: Project[], filename: string): void {
        const rows = projects.map((p, i) => ({
            'Sl.': i + 1,
            'GP Village':   p.loc || p.village || '',
            'Project Name': p.name,
            'Scheme':       p.scheme || '',
            'Sanctioned Year': p.sanctionedYear || '',
            'Est. Cost (\u20b9L)': p.cost ?? 0,
            'Spent (\u20b9L)':   p.spent ?? 0,
            'Status':       p.status,
            'Progress (%)': p.physical ?? 0,
            'Executor':     p.executorName || p.executingAgency || '',
            'Contact':      p.executorNumber || '',
            'Remarks':      p.remarks || '',
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        ws['!cols'] = [
            { wch: 4 }, { wch: 16 }, { wch: 45 }, { wch: 18 },
            { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 12 },
            { wch: 22 }, { wch: 14 }, { wch: 28 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Field Execution');
        XLSX.writeFile(wb, `${filename}.xlsx`);
    }

    // ── Private helpers ────────────────────────────────────────────────

    /**
     * Compare block vs GP/village labels ignoring Dhamanagar/Dhamnagar spelling
     * and punctuation so we do not create a duplicate "GP" row that is really the block name.
     */
    private normalizeGeoKey(label: string): string {
        return (label || '')
            .toLowerCase()
            .replace(/dhamanagar|dhamnagar/g, 'dham')
            .replace(/[\s_,.-]+/g, '')
            .replace(/[^a-z0-9]/g, '');
    }

    /**
     * When a project's village/location repeats the block (common import mistake),
     * fold it into the primary local GP bucket instead of showing a fake duplicate row.
     */
    private villageBucket(block: string, p: Project): string {
        const raw = (p.gp || p.village || p.loc || '').trim();
        const v = raw || 'Unassigned';
        const bk = this.normalizeGeoKey(block);
        const vk = this.normalizeGeoKey(v);
        if (bk.length > 0 && vk === bk) {
            return 'Unassigned';
        }
        // NAC projects belong under Dhamanagar NAC block, not as a GP under Dhamanagar Block
        if (vk === 'nac' || vk === 'dnrnac') {
            return 'Unassigned';
        }
        const titled = this.toTitleCase(v);
        // Resolve known spelling variants to canonical GP name
        const aliasKey = v.toLowerCase().replace(/\s+/g, ' ').trim();
        return GP_NAME_ALIASES[aliasKey] ?? titled;
    }

    private toTitleCase(s: string): string {
        return s.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    }

    private groupByBlockVillage(projects: Project[]): BlockGroup[] {
        const blockMap = new Map<string, Map<string, Project[]>>();

        // Seed all fixed blocks so every block always appears
        for (const block of FIELD_EXECUTION_BLOCKS) {
            blockMap.set(block, new Map());
        }

        // Seed canonical GPs per block so all GPs appear even with 0 projects
        for (const [block, gps] of Object.entries(BLOCK_GP_MASTER)) {
            const villageMap = blockMap.get(block);
            if (!villageMap) continue;
            for (const gp of gps) {
                if (!villageMap.has(gp)) villageMap.set(gp, []);
            }
        }

        for (const p of projects) {
            const block   = p.block ?? '';
            const village = this.villageBucket(block, p);

            if (!blockMap.has(block)) continue;
            const villageMap = blockMap.get(block)!;
            if (!villageMap.has(village)) villageMap.set(village, []);
            villageMap.get(village)!.push(p);
        }

        const alphaSort = (a: string, b: string) =>
            (a || '').trim().localeCompare((b || '').trim(), undefined, { sensitivity: 'base' });

        return Array.from(blockMap.entries())
            .sort(([a], [b]) => alphaSort(a, b))
            .map(([block, villageMap]) => {
                const villages: VillageGroup[] = Array.from(villageMap.entries())
                    .sort(([a], [b]) => alphaSort(a, b))
                    .map(([village, projs]) => {
                        const sortedProjs = [...projs].sort((a, b) =>
                            alphaSort(a.name || '', b.name || '') ||
                            alphaSort(a.village || a.loc || '', b.village || b.loc || '')
                        );
                        const villageStats = aggregateFeStats(sortedProjs);
                        const totalV = villageStats.totalProjects;
                        const {
                            sanctionedAmount: sanctionedV,
                            onTrackCount: onTrackV,
                            onTrackPercent: onTrackPctV,
                            inProgressCount: inProgressV,
                            inProgressPercent: inProgressPctV,
                            delayedCount: delayedV,
                            delayedPercent: delayedPctV,
                            avgProgress: avgProgressV,
                        } = villageStats;

                        // PAI Score logic (Mocked based on progress and delay)
                        const pai = totalV > 0 ? Math.round((onTrackV / totalV) * 60 + (avgProgressV / 100) * 40) : 0;
                        const category = pai > 70 ? 'Good' : pai > 40 ? 'Moderate' : 'Poor';
                        const trend = pai % 2 === 0 ? 'up' : 'down'; // mock trend

                        return {
                            village,
                            projects: sortedProjs,
                            totalProjects: totalV,
                            sanctionedAmount: sanctionedV,
                            onTrackCount: onTrackV,
                            onTrackPercent: onTrackPctV,
                            inProgressCount: inProgressV,
                            inProgressPercent: inProgressPctV,
                            delayedCount: delayedV,
                            delayedPercent: delayedPctV,
                            avgProgress: avgProgressV,
                            paiScore: pai,
                            paiCategory: category,
                            paiTrend: trend,
                            lastUpdated: totalV > 0 ? 'Today' : '-',
                            statusBadge: delayedV > totalV * 0.2 ? 'High Delay' : (category as any)
                        };
                    });
                
                const allProjs = villages.flatMap(v => v.projects);
                const blockStats = aggregateFeStats(allProjs);
                const avgPai = villages.length > 0 ? (villages.reduce((s, v) => s + v.paiScore, 0) / villages.length) : 0;

                return {
                    block,
                    villages,
                    totalProjects: blockStats.totalProjects,
                    sanctionedAmount: blockStats.sanctionedAmount,
                    onTrackCount: blockStats.onTrackCount,
                    onTrackPercent: blockStats.onTrackPercent,
                    inProgressCount: blockStats.inProgressCount,
                    inProgressPercent: blockStats.inProgressPercent,
                    delayedCount: blockStats.delayedCount,
                    delayedPercent: blockStats.delayedPercent,
                    avgProgress: blockStats.avgProgress,
                    avgPaiScore: Math.round(avgPai),
                    lastUpdated: blockStats.totalProjects > 0 ? 'Today' : '-',
                    statusBadge: blockStats.delayedCount > blockStats.totalProjects * 0.2
                        ? 'High Delay'
                        : blockStats.avgProgress > 70 ? 'Good Progress' : 'Low Performance'
                };
            });
    }
}


